/**
 * ProjectList Component
 * AIDLC Administrative Backend
 *
 * Left panel (280px) project list showing all projects as vertical cards.
 * Fetches from /api/projects/overview.
 */

import { useState, useEffect } from 'react';
import { ProjectCard } from './ProjectCard';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { fetchProjectsOverview } from '../../services/projectManagementService';
import type { ProjectOverview } from '../../types/projects';

interface ProjectListProps {
  selectedProjectId: string | null;
  onSelectProject: (project: ProjectOverview) => void;
}

export function ProjectList({ selectedProjectId, onSelectProject }: ProjectListProps) {
  const [projects, setProjects] = useState<ProjectOverview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchProjectsOverview();
        if (!cancelled) setProjects(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load projects');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Auto-select first project if none selected
  useEffect(() => {
    if (!selectedProjectId && projects.length > 0) {
      onSelectProject(projects[0]);
    }
  }, [projects, selectedProjectId, onSelectProject]);

  if (isLoading) {
    return (
      <div className="p-4">
        <LoadingSpinner message="Loading projects..." size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-xs">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Projects</h2>
        <p className="text-[10px] text-gray-400 mt-0.5">{projects.length} project{projects.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Project cards */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {projects.length === 0 ? (
          <div className="text-center py-8 text-xs text-gray-400 italic">No projects found.</div>
        ) : (
          projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isSelected={project.id === selectedProjectId}
              onClick={() => onSelectProject(project)}
            />
          ))
        )}
      </div>
    </div>
  );
}
